import { getCurrentUser } from "aws-amplify/auth";

import { ObjectValues } from "@/types/utilities";

// Role and Industry are the onboarding profile answers. The backend stores them
// as free-form strings, so these ids are the frontend's own vocabulary: safe to
// add to or retire without a backend deploy. Anything reading a stored value
// must treat it as `string | null` — accounts created before the questions
// existed have neither, and either question can be skipped.
export const UserRoleE = {
	CxO: "cxo",
	Finance: "finance",
	Engineering: "engineering",
	Other: "other",
} as const;
export type UserRole = ObjectValues<typeof UserRoleE>;

export const UserIndustryE = {
	QuantitativeFinance: "quant",
	DigitalBanking: "banking",
	EngineeringDesign: "engdesign",
	Robotics: "robotics",
	IndustrialAutomation: "automation",
	MachineLearningAI: "mlai",
	SupplyChainPlanning: "supplychain",
	HybridQuantumComputing: "quantum",
	Other: "other",
} as const;
export type UserIndustry = ObjectValues<typeof UserIndustryE>;

export const USER_ROLE_LABELS: Record<UserRole, string> = {
	[UserRoleE.CxO]: "CxO / Leadership",
	[UserRoleE.Finance]: "Finance / Billing",
	[UserRoleE.Engineering]: "Engineering",
	[UserRoleE.Other]: "Other",
};

export const USER_INDUSTRY_LABELS: Record<UserIndustry, string> = {
	[UserIndustryE.QuantitativeFinance]: "Quantitative Finance",
	[UserIndustryE.DigitalBanking]: "Digital Banking",
	[UserIndustryE.EngineeringDesign]: "Engineering Design",
	[UserIndustryE.Robotics]: "Robotics",
	[UserIndustryE.IndustrialAutomation]: "Industrial Automation",
	[UserIndustryE.MachineLearningAI]: "Machine Learning & AI",
	[UserIndustryE.SupplyChainPlanning]: "Supply Chain Planning",
	[UserIndustryE.HybridQuantumComputing]: "Hybrid Quantum Computing",
	[UserIndustryE.Other]: "Other",
};

export type UserPreferences = {
	Editor_Theme: string;
	Editor_Layout_VariableViewer: string;
	Execution_DefaultCore: string;
	Execution_DefaultReferenceCore: string;
	UpdatedAt: number;
	Editor_Execution_DataSources: string; //FIXME:
	Editor_Execution_Arguments: string;
	Editor_Compilation_Flags: string;
	Editor_Execution_Core: string;
	Editor_Execution_CodeLanguage: string;
	Editor_SourceCode: string;
	Editor_Layout: string;
	Execution_DefaultDataSources: string;
};

export type OnboardingState = {
	Tour_QuickStart_Completed: boolean;
	Tour_Detailed_Completed: boolean;
	Onboarding_APIKey_Completed: boolean;
	Onboarding_CustomCore_Completed: boolean;
	Onboarding_DataSource_Completed: boolean;
	Onboarding_Repository_Completed: boolean;
	Onboarding_FirstTask_Completed: boolean;
	Onboarding_PreviewTask_Completed: boolean;
};

export type UserResourceUsage = {
	APIAccessCount: number;
	ConcurrentTaskCount: number;
	ConcurrentBuildCount: number;
	DataSourceCount: number;
	PlotCount: number;
	RepositoryCount: number;
	BucketCount: number;
	CloudStorageBytes: number;
	CoreCount: number;
	DataDriveCount: number;
	DynamicInstructionCount: number;
	GatewayCount: number;
	KeyCount: number;
	RegistryStorageBytes: number;
	ResetsAt: number;
	UpdatedAt: number;
};

export type User = {
	Preferences: Partial<UserPreferences>;
	OnboardingState?: Partial<OnboardingState>;
	Role?: string | null;
	Industry?: string | null;
	ResourceUsage: UserResourceUsage;
	UserID: string;
	Object: string;
	CreatedAt: number;
	Username: string;
	Tier?: string;
	Allowance?: Record<string, number>;
	CoreClasses?: { Name: string; SupportedMicroarchitectures: string[] }[];
	Microarchitectures?: {
		Name: string;
		DistributionTracking: boolean;
		CustomPrecision: boolean;
		PrecisionMustBePowerOfTwo: boolean;
		CorrelationTracking: string[];
	}[];
	FieldConstraints?: {
		MemorySize: { Minimum: number; Maximum: number };
		Precision: { Minimum: number; Maximum: number };
	};
};

export type UserCustomization = {
	UserID: string;
	LogoURL?: string;
	AtomicNetworks: string[];
	Organizations?: string[];
};

export type CurrentUser = {
	email: string;
	name: string;
	affiliation: string;
	userID: string;
	avatar: string;
};

export type AccountLogItem = {
	timestamp: string;
	eventType: string;
	userName: string;
	eventResponse: string;
	ipAddress?: string;
	deviceName?: string;
	city?: string;
	country?: string;
};

export type AccountLogsResponse = {
	logs: AccountLogItem[];
	queryTimeRange: {
		startTime: number;
		endTime: number;
	};
	totalResults: number;
};

export type AWSAuthenticatedUserObject = Awaited<ReturnType<typeof getCurrentUser>> & {
	attributes?: Record<string, string>;
	refreshSession?: (...args: any[]) => void;
	getSignInUserSession?: () => any;
};
export type AWSAuthenticatedSession = any;
